import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/thcgu0ac_UI%20Letter%20Head.png";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Business Model", href: "#business-model" },
  { label: "Industries", href: "#industries" },
  { label: "Why Us", href: "#why-us" },
  { label: "Partner With Us", href: "#partner" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="#hero" onClick={() => handleNav("#hero")} className="flex items-center gap-3 shrink-0">
            <img
              src={LOGO_URL}
              alt="Ultimate Infusions"
              className="h-12 lg:h-14 w-auto rounded-lg"
              data-testid="navbar-logo"
            />
            <div className={`flex flex-col gap-0.5 w-[90px] lg:w-[110px] transition-colors ${
              scrolled ? "text-[#0B1D3A]" : "text-white"
            }`} style={{ fontFamily: "'Controller W01 Three', sans-serif" }}>
              <span className="flex justify-between text-base lg:text-lg leading-none">
                {'ULTIMATE'.split('').map((c, i) => <span key={i}>{c}</span>)}
              </span>
              <span className="flex justify-between text-base lg:text-lg leading-none text-[#C8D616]">
                {'INFUSIONS'.split('').map((c, i) => <span key={i}>{c}</span>)}
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  scrolled
                    ? "text-slate-600 hover:text-[#0B1D3A] hover:bg-slate-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center">
            <Button
              onClick={() => handleNav("#contact")}
              className="rounded-full bg-[#C8D616] text-[#0B1D3A] hover:bg-[#B5C314] text-sm font-semibold"
              data-testid="nav-partner-btn"
            >
              Contact
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-md"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className={scrolled ? "text-slate-800" : "text-white"} size={24} />
            ) : (
              <Menu className={scrolled ? "text-slate-800" : "text-white"} size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-3 flex flex-col gap-2">
                <Button
                  onClick={() => handleNav("#contact")}
                  className="w-full rounded-full bg-[#C8D616] text-[#0B1D3A] hover:bg-[#B5C314] font-semibold"
                  data-testid="mobile-partner-btn"
                >
                  Contact
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
